import React, { useState, useEffect } from "react";
import { MasterComplianceCalendarEntity } from "../../domain/entities/MasterComplianceCalendarEntity";
import { ComplianceTypeEntity } from "../../domain/entities/ComplianceTypeEntity";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";

interface CalendarEntryFormProps {
  complianceTypes: ComplianceTypeEntity[];
  initialData?: MasterComplianceCalendarEntity | null;
  selectedComplianceTypeId?: number | null;
  onSubmit: (data: Partial<MasterComplianceCalendarEntity>) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export const CalendarEntryForm: React.FC<CalendarEntryFormProps> = ({
  complianceTypes,
  initialData,
  selectedComplianceTypeId,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    compliance_type_id: selectedComplianceTypeId || 0,
    compliance_form_name: "",
    period: "",
    period_start_date: "",
    period_end_date: "",
    due_date: "",
    authority: "",
    is_active: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        compliance_type_id: initialData.compliance_type_id,
        compliance_form_name: initialData.compliance_form_name || "",
        period: initialData.period || "",
        period_start_date: initialData.period_start_date || "",
        period_end_date: initialData.period_end_date || "",
        due_date: initialData.due_date || "",
        authority: initialData.authority || "",
        is_active: initialData.is_active ?? true,
      });
    } else if (selectedComplianceTypeId) {
      setFormData((prev) => ({
        ...prev,
        compliance_type_id: selectedComplianceTypeId,
      }));
      // Auto-fill authority from selected compliance type
      const selectedType = complianceTypes.find(
        (t) => t.id === selectedComplianceTypeId
      );
      if (selectedType) {
        setFormData((prev) => ({
          ...prev,
          authority: selectedType.authority,
        }));
      }
    }
  }, [initialData, selectedComplianceTypeId, complianceTypes]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : type === "number"
          ? parseInt(value) || 0
          : value,
    }));

    // Update authority when compliance type changes
    if (name === "compliance_type_id") {
      const selectedType = complianceTypes.find(
        (t) => t.id === parseInt(value)
      );
      if (selectedType) {
        setFormData((prev) => ({
          ...prev,
          compliance_type_id: parseInt(value),
          authority: selectedType.authority,
        }));
      }
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const newErrors: Record<string, string> = {};
    if (!formData.compliance_type_id) {
      newErrors.compliance_type_id = "Compliance type is required";
    }
    if (!formData.compliance_form_name.trim()) {
      newErrors.compliance_form_name = "Compliance form name is required";
    }
    if (!formData.period.trim()) {
      newErrors.period = "Period is required";
    }
    if (!formData.period_start_date) {
      newErrors.period_start_date = "Period start date is required";
    }
    if (!formData.period_end_date) {
      newErrors.period_end_date = "Period end date is required";
    }
    if (!formData.due_date) {
      newErrors.due_date = "Due date is required";
    }
    if (!formData.authority.trim()) {
      newErrors.authority = "Authority is required";
    }

    // Validate dates
    if (formData.period_start_date && formData.period_end_date) {
      if (
        new Date(formData.period_start_date) >
        new Date(formData.period_end_date)
      ) {
        newErrors.period_end_date = "Period end date must be after start date";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await onSubmit(formData);
    } catch (err: any) {
      alert(err.message || "Failed to save calendar entry");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 text-left">
      <div className="flex flex-col gap-2 text-left">
        <label className="text-body-bold font-body-bold text-default-font text-left">
          Compliance Type <span className="text-red-500">*</span>
        </label>
        <select
          name="compliance_type_id"
          value={formData.compliance_type_id}
          onChange={handleChange}
          disabled={true}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 text-left"
        >
          <option value={0}>Select compliance type</option>
          {complianceTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.compliance_name} ({type.compliance_type})
            </option>
          ))}
        </select>
        {errors.compliance_type_id && (
          <span className="text-caption font-caption text-red-600 text-left">
            {errors.compliance_type_id}
          </span>
        )}
      </div>

      <TextField
        label="Compliance Form Name *"
        helpText={errors.compliance_form_name || ""}
        error={!!errors.compliance_form_name}
      >
        <TextField.Input
          name="compliance_form_name"
          value={formData.compliance_form_name}
          onChange={handleChange}
          placeholder="e.g., GSTR-3B, GSTR-1, Form 24Q"
          disabled={loading}
        />
      </TextField>

      <TextField
        label="Period *"
        helpText={
          errors.period ||
          "Format: YYYY-MM for monthly, YYYY-Q1/Q2/Q3/Q4 for quarterly"
        }
        error={!!errors.period}
      >
        <TextField.Input
          name="period"
          value={formData.period}
          onChange={handleChange}
          placeholder="e.g., 2025-01, 2025-Q1"
          disabled={loading}
        />
      </TextField>

      <div className="grid grid-cols-2 gap-4">
        <TextField
          label="Period Start Date *"
          helpText={errors.period_start_date || ""}
          error={!!errors.period_start_date}
        >
          <TextField.Input
            name="period_start_date"
            type="date"
            value={formData.period_start_date}
            onChange={handleChange}
            disabled={loading}
          />
        </TextField>

        <TextField
          label="Period End Date *"
          helpText={errors.period_end_date || ""}
          error={!!errors.period_end_date}
        >
          <TextField.Input
            name="period_end_date"
            type="date"
            value={formData.period_end_date}
            onChange={handleChange}
            disabled={loading}
          />
        </TextField>
      </div>

      <TextField
        label="Due Date *"
        helpText={errors.due_date || ""}
        error={!!errors.due_date}
      >
        <TextField.Input
          name="due_date"
          type="date"
          value={formData.due_date}
          onChange={handleChange}
          disabled={loading}
        />
      </TextField>

      <TextField
        label="Authority *"
        helpText={errors.authority || ""}
        error={!!errors.authority}
      >
        <TextField.Input
          name="authority"
          value={formData.authority}
          onChange={handleChange}
          placeholder="e.g., GST, TDS, PF, ESI"
          disabled={true}
        />
      </TextField>

      <div className="flex flex-col gap-2 text-left">
        <label className="flex items-center gap-2 text-left">
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            disabled={loading}
            className="w-4 h-4"
          />
          <span className="text-body-bold font-body-bold text-default-font">
            Active
          </span>
        </label>
      </div>

      <div className="flex gap-2 mt-4">
        <Button type="submit" variant="brand-primary" loading={loading}>
          {initialData ? "Update" : "Create"}
        </Button>
        <Button
          type="button"
          variant="neutral-secondary"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};
